import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import BackButton from '../components/BackButton';

const Photography: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-black text-white font-rajdhani relative overflow-hidden">
      {/* Background effects */}
      <div className="fixed inset-0 z-[1] opacity-30">
        <div className="absolute inset-0 bg-gradient-to-br from-cyan-900/20 to-black"></div>
      </div>

      {/* HUD Grid Overlay */}
      <div className="fixed inset-0 z-[2] pointer-events-none">
        <div className="absolute inset-0 bg-cyber-grid opacity-10" style={{ backgroundSize: '50px 50px' }}></div>
        {/* Corner brackets */}
        <div className="absolute top-16 left-4 w-8 h-8 border-l-2 border-t-2 border-cyan-400/50"></div>
        <div className="absolute top-16 right-4 w-8 h-8 border-r-2 border-t-2 border-cyan-400/50"></div>
        <div className="absolute bottom-16 left-4 w-8 h-8 border-l-2 border-b-2 border-cyan-400/50"></div>
        <div className="absolute bottom-16 right-4 w-8 h-8 border-r-2 border-b-2 border-cyan-400/50"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 pt-24 pb-16 px-4">
        <BackButton onClick={handleBack} />
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="text-cyan-400 font-mono text-lg mb-4 cyber-glow">
            bash@neural-core:~$ cat /visual_gallery/manifest.json
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
            PHOTOGRAPHY_HUD
          </h1>
          <p className="text-gray-400 text-xl max-w-4xl mx-auto">
            Visual Documentation • Light Capture Systems • Digital Archives
          </p>
        </motion.div>

        <div className="max-w-7xl mx-auto">
          {/* Camera Systems Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-cyan-900/20 to-cyan-700/10 border border-cyan-400/30 p-8 rounded-lg mb-8"
          >
            <h2 className="text-cyan-400 font-mono text-2xl mb-6">EQUIPMENT_STATUS</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-cyan-400/20 rounded-full flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-cyan-400 rounded"></div>
                </div>
                <h3 className="text-white font-semibold mb-2">PRIMARY_DSLR</h3>
                <p className="text-gray-400 text-sm">Canon EOS R6 Mark II</p>
                <div className="mt-2 text-green-400 text-xs">STATUS: ACTIVE</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-purple-400/20 rounded-full flex items-center justify-center">
                  <div className="w-6 h-6 border border-purple-400 rounded-full"></div>
                </div>
                <h3 className="text-white font-semibold mb-2">MIRRORLESS</h3>
                <p className="text-gray-400 text-sm">Sony Alpha A7 IV</p>
                <div className="mt-2 text-yellow-400 text-xs">STATUS: STANDBY</div>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 mx-auto mb-4 bg-orange-400/20 rounded-full flex items-center justify-center">
                  <div className="w-4 h-6 border border-orange-400"></div>
                </div>
                <h3 className="text-white font-semibold mb-2">MOBILE_UNIT</h3>
                <p className="text-gray-400 text-sm">iPhone 15 Pro Max</p>
                <div className="mt-2 text-green-400 text-xs">STATUS: READY</div>
              </div>
            </div>
          </motion.div>

          {/* Gallery Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {[
              { category: "PORTRAITS", count: 47, color: "cyan" },
              { category: "LANDSCAPES", count: 32, color: "green" },
              { category: "STREET", count: 89, color: "purple" },
              { category: "MACRO", count: 21, color: "orange" },
              { category: "NIGHT", count: 56, color: "blue" },
              { category: "EVENTS", count: 134, color: "pink" }
            ].map((album, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + (i * 0.1) }}
                className={`bg-black/60 border border-${album.color}-400/30 p-6 rounded-lg hover:border-${album.color}-400 transition-all duration-300 cursor-pointer group`}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`w-3 h-3 bg-${album.color}-400 rounded-full animate-pulse`}></div>
                  <div className={`text-${album.color}-400 font-mono text-sm`}>
                    {album.count} IMAGES
                  </div>
                </div>
                
                {/* Simulated image grid */}
                <div className="grid grid-cols-3 gap-1 mb-4">
                  {[...Array(9)].map((_, idx) => (
                    <motion.div
                      key={idx}
                      className={`aspect-square bg-gradient-to-br from-${album.color}-400/20 to-gray-800/40 rounded`}
                      whileHover={{ scale: 1.05 }}
                    ></motion.div>
                  ))}
                </div>
                
                <h3 className={`text-${album.color}-400 font-mono text-lg mb-2`}>
                  {album.category}
                </h3>
                <p className="text-gray-400 text-sm">
                  High resolution captures • RAW format • Color graded
                </p>
                
                <motion.div
                  className={`mt-4 text-xs text-${album.color}-400 opacity-0 group-hover:opacity-100 transition-opacity`}
                >
                  [CLICK_TO_VIEW_GALLERY] &gt;&gt;
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* Technical Specifications */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="bg-black/60 border border-green-400/30 p-6 rounded-lg"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
              <h3 className="text-green-400 font-mono text-xl">TECHNICAL_SPECS</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div>
                <h4 className="text-white font-semibold mb-3">Resolution</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• 24-45 Megapixels</div>
                  <div>• 8K Video Capable</div>
                  <div>• RAW + JPEG Format</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Lenses</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• 24-70mm f/2.8 L</div>
                  <div>• 70-200mm f/2.8 L</div>
                  <div>• 16-35mm f/2.8 L</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Specializations</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Low Light Photography</div>
                  <div>• Portrait & Event</div>
                  <div>• Architectural</div>
                </div>
              </div>
              <div>
                <h4 className="text-white font-semibold mb-3">Post-Processing</h4>
                <div className="space-y-2 text-gray-300">
                  <div>• Adobe Lightroom CC</div>
                  <div>• Photoshop 2024</div>
                  <div>• Color Grading</div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Photography;